import * as React from 'react';
import { useCallback, useState } from 'react';
import cn from 'classnames';
import MediaQuery from 'react-responsive';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../../theme';
import { MOBILE_MIN_WIDTH } from '../../../utils';
import SlideBottom from '../../../SlideBottom';
import { SELECTED_DISHES_CATEGORY_ID, ALL_DISHES_CATEGORY_ID } from '../../constants';
import List, { ListItem } from './List/List';
import CloseIcon from '../../../CloseIcon/components/CloseIcon';
import { CdnIcon } from '@shared/ui';

interface Props {
	categories: string[];
	selectedCategory: number;
	onSelect: (id: number) => void;
	numOfSelectedMeals: number;
	defaultGroupPlacement?: 'start' | 'end';
}

const Categories: React.FunctionComponent<Props> = props => {
	const { CategoriesStyles: theme } = useTheme('MealService');
	const { t } = useTranslation('MealService');
	const [isOpenSlide, setOpenSlide] = useState(false);

	const toggleOpenSlide = () => setOpenSlide(!isOpenSlide);

	const mobileButtonTitle = React.useMemo(() => {
		switch (props.selectedCategory) {
			case ALL_DISHES_CATEGORY_ID:
				return t('All');

			case SELECTED_DISHES_CATEGORY_ID:
				return t('Selected dishes');

			default:
				return props.categories[props.selectedCategory];
		}
	}, [props.categories, props.selectedCategory]);

	const defaultCategory = {
		id: ALL_DISHES_CATEGORY_ID,
		title: t('All')
	};

	const categories: ListItem[] = React.useMemo(() => {
		const res =
			props.categories.length === 1
				? [defaultCategory]
				: [...props.categories.map((title, index) => ({ id: index, title }))];

		if (!res.includes(defaultCategory) && props.categories.length > 1) {
			if (props.defaultGroupPlacement === 'end') {
				res.push(defaultCategory);
			} else {
				res.unshift(defaultCategory);
			}
		}

		if (props.numOfSelectedMeals > 0) {
			res.push({
				id: SELECTED_DISHES_CATEGORY_ID,
				title: `${t('Selected dishes')} (${props.numOfSelectedMeals})`
			});
		}

		return res;
	}, [props.categories, props.defaultGroupPlacement, props.numOfSelectedMeals]);

	const mobileOnSelect = useCallback(
		(id: number) => {
			props.onSelect(id);
			toggleOpenSlide();
		},
		[props.onSelect, isOpenSlide]
	);

	return (
		<div className={theme.wrapper}>
			<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
				<List categories={categories} selectedCategory={props.selectedCategory} onSelect={props.onSelect} />
			</MediaQuery>

			<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
				<button className={cn(theme.category__button)} onClick={toggleOpenSlide}>
					<span>{mobileButtonTitle}</span>
					<CdnIcon className={theme.button__icon} icon="Arrow/Expand_more" />
				</button>

				<SlideBottom isOpen={isOpenSlide} onClose={toggleOpenSlide}>
					<div className={theme.mobile__header}>
						<span className={theme.mobile__title}>{t('Select menu')}</span>
						<CloseIcon isGray={true} onClick={toggleOpenSlide} />
					</div>

					<List categories={categories} selectedCategory={props.selectedCategory} onSelect={mobileOnSelect} />
				</SlideBottom>
			</MediaQuery>
		</div>
	);
};

export default Categories;
